import {CodeViewError} from '@github-ui/code-view-shared/components/CodeViewError'
import {DuplicateOnKeydownButton} from '@github-ui/code-view-shared/components/DuplicateOnKeydownButton'
import {ActionProvider, type FilesPageAction} from '@github-ui/code-view-shared/contexts/ActionContext'
import {AllShortcutsEnabledProvider} from '@github-ui/code-view-shared/contexts/AllShortcutsEnabledContext'
import {CodeViewBannersProvider} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {CopilotSettingsProvider} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {MarketplaceEnabledProvider} from '@github-ui/code-view-shared/contexts/MarketplaceEnabledContext'
import {OpenPanelProvider} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {useCanonicalObject} from '@github-ui/code-view-shared/hooks/use-canonical-object'
import {useFilePagePayload} from '@github-ui/code-view-shared/hooks/use-file-page-payload'
import {extractFileTree} from '@github-ui/code-view-shared/utilities/make-payload'
import {
  type FilePagePayload,
  isBlamePayload,
  isBlobPayload,
  isDeletePayload,
  isEditPayload,
  isTreePayload,
} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {FileTreeControlProvider, ReposFileTreePane, useTreePane} from '@github-ui/repos-file-tree-view'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {sendCustomMetric} from '@github-ui/stats'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useDisableUserContentScrolling} from '@github-ui/use-disable-user-content-scrolling'
import {useHideFooter} from '@github-ui/use-hide-footer'
import {useWebVitals} from '@github-ui/use-web-vitals'
import {Heading, SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import React, {lazy, Suspense, useCallback, useEffect, useState} from 'react'

import {symbolsHeaderId} from '../components/blob/BlobContent/CodeNav/CodeNavSymbolNavigation'
import {ScrollMarksContainer} from '../components/blob/BlobContent/CodeNav/ScrollMarksContainer'
import {BlobViewContent} from '../components/BlobViewContent'
import CodeViewBanners from '../components/CodeViewBanners'
import {DeleteViewContent} from '../components/delete/DeleteViewContent'
import FileTreeViewContent from '../components/FileTreeViewContent'
import CodeViewHeader from '../components/headers/CodeViewHeader'
import {PermalinkShortcut} from '../components/PermalinkShortcut'
import {LoadingFallback} from '../components/SuspenseFallback'
import {DeferredASTProvider, useLoadDeferredAST} from '../contexts/DeferredASTContext'
import {DeferredMetadataProvider, useLoadDeferredMetadata} from '../contexts/DeferredMetadataContext'
import {FindInFileOpenProvider} from '../contexts/FindInFileOpenContext'
import {SearchTermProvider} from '../contexts/SearchTermContext'
import {CurrentBlameProvider} from '../hooks/CurrentBlame'
import {CurrentBlobProvider} from '../hooks/CurrentBlob'
import {CurrentStyledBlobProvider} from '../hooks/CurrentStyledBlob'
import {updateExpandPreferences} from '../hooks/use-update-panel-expand-preferences'
import {textAreaId} from '../utilities/lines'
import styles from './CodeViewLegacy.module.css'

const BlobEditor = lazy(() => import('../components/blob-edit/BlobEditor'))

export function CodeViewEntrypoint({initialPayload}: {initialPayload?: FilePagePayload}) {
  const payload = useFilePagePayload(initialPayload)
  return <CodeViewLegacy payload={payload} isDataRouter={false} />
}

const updateTreeExpandedPreference = (isTreeExpanded: boolean) => {
  updateExpandPreferences(isTreeExpanded, null, null)
}

export function CodeViewLegacy({payload, isDataRouter = true}: {payload: FilePagePayload; isDataRouter?: boolean}) {
  const repo = useCurrentRepository()
  const {findFileWorkerPath} = useReposAppPayload()
  const {path} = payload
  const refInfo = useCanonicalObject(payload.refInfo)
  const isEdit = isEditPayload(payload)
  const isBlob = isBlobPayload(payload)
  const isBlame = isBlamePayload(payload)
  const isDelete = isDeletePayload(payload)
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null)
  const deferredMetadata = useLoadDeferredMetadata(repo, refInfo, path, payload.error?.httpStatus === 404)
  const deferredAST = useLoadDeferredAST(
    repo,
    refInfo,
    path,
    payload.error?.httpStatus === 404,
    isBlob || isBlame ? (payload.blob.rawLines?.length ?? 100000) : 100000,
  )
  const treeRef = React.useRef<HTMLDivElement>(null)
  // when user presses cmd+f6 this controls whether we focus the tree or the content
  const contentFocusedRef = React.useRef(false)
  const textAreaFocusedRef = React.useRef(false)
  // when we focus the content we can try to return focus to the element which previously had it
  const contentFocusTargetRef = React.useRef<HTMLElement | null>(null)
  // when we focus the tree we can try to return focus to the element which previously had it
  const treeFocusTargetRef = React.useRef<HTMLElement | null>(null)
  const reposFileTreeId = 'repos-file-tree'
  const openPanelRef = React.useRef<string | undefined>(undefined)

  useWebVitals('hpc', event => {
    if (!isTreePayload(payload)) return
    if (!event.detail.hpc) return
    sendCustomMetric({
      name: 'CODE_VIEW_TREE_HPC',
      value: event.detail.hpc,
      tags: {
        app: 'react-code-view',
        soft: String(event.detail.soft),
        ssr: String(event.detail.ssr),
        mechanism: String(event.detail.mechanism),
      },
    })
  })

  useWebVitals('inp', event => {
    if (!isTreePayload(payload)) return
    if (!event.detail.inp) return
    sendCustomMetric({
      name: 'CODE_VIEW_TREE_INP',
      value: event.detail.inp,
      tags: {
        app: 'react-code-view',
        inpInteractionType: event.detail.inpInteractionType || 'unknown',
        inpEventType: event.detail.inpEventType || 'unknown',
        inpBottleneck: event.detail.inpBottleneck || 'unknown',
      },
    })
  })

  const {toggleFocusedPaneShortcut} = useShortcut()
  function toggleFocus() {
    const symbolsPaneElement = document.getElementById(symbolsHeaderId)
    const textAreaElement = document.getElementById(textAreaId)
    if (document.activeElement?.id === textAreaId) {
      textAreaFocusedRef.current = true
    }
    // the user may have moved focus from where we last put it
    if (contentRef?.contains(document.activeElement) && !textAreaFocusedRef.current) {
      //the content (but not the text area) is focused
      contentFocusedRef.current = true
    } else if (treeRef.current?.contains(document.activeElement)) {
      contentFocusedRef.current = false
    }
    if (!contentFocusedRef.current && !textAreaFocusedRef.current) {
      // focus the text area
      const focusTarget = textAreaElement || contentRef
      treeFocusTargetRef.current = treeRef.current?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      focusTarget?.focus()
    } else if (textAreaFocusedRef.current) {
      //focus the content
      const focusTarget = contentFocusTargetRef.current || symbolsPaneElement || contentRef
      contentFocusedRef.current = true
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    } else {
      // focus the tree
      const focusTarget = treeFocusTargetRef.current || treeRef.current
      contentFocusTargetRef.current = contentRef?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    }
  }

  // While this component is mounted, disable user automatic user content scrolling
  // based on the URL hash. This scrolling is handled by the individual components
  // that require it.
  useDisableUserContentScrolling()

  // While this component is mounted, hide the standard footer. The reason we
  // must do this in javascript is because the footer is retained across Turbo
  // navigations, so if we omit it in the controller, it may still be present
  // under some circumstances.
  useHideFooter(true)

  const fileTree = React.useMemo(
    () => extractFileTree(payload),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload.path, payload.refInfo.currentOid],
  )

  const {isTreeExpanded, collapseTree, expandTree, treeToggleElement, treeToggleRef, searchBoxRef} = useTreePane(
    reposFileTreeId,
    openPanelRef,
    payload.treeExpanded,
    textAreaId,
    updateTreeExpandedPreference,
  )

  // When a tree item is selected, collapse the tree if the screen is small
  const onTreeItemSelected = useCallback(() => {
    if (window.innerWidth < ScreenSize.large) {
      collapseTree({focus: null})
    }
  }, [collapseTree])

  const [validCodeNav, setValidCodeNav] = useState(true)

  useEffect(() => {
    let frameId: number | undefined
    // scroll to top of code nav on file change if not going to a specific line and already scrolled down
    if (!window.location.hash && window.scrollY > 0) {
      const codeViewHeader = document.querySelector('#StickyHeader') as HTMLElement
      if (codeViewHeader) {
        codeViewHeader.style.position = 'relative'
        codeViewHeader.scrollIntoView()
        frameId = requestAnimationFrame(() => {
          codeViewHeader.style.position = 'sticky'
        })
      }
    }
    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
    }
  }, [payload.path])

  const {codeCenterOption} = useCodeViewOptions()

  const onFindFilesShortcut = React.useCallback(() => {
    if (window.innerWidth < ScreenSize.large) {
      expandTree({focus: 'search'})
    }
  }, [expandTree])

  let action: FilesPageAction

  if (isEdit) {
    if (payload.editInfo.isNewFile) {
      action = 'new'
    } else {
      action = 'edit'
    }
  } else if (isBlame) {
    action = 'blame'
  } else if (isBlob) {
    action = 'blob'
  } else {
    action = 'tree'
  }

  return (
    <DeferredMetadataProvider {...deferredMetadata}>
      <DeferredASTProvider {...deferredAST}>
        <PathProvider path={path}>
          <ActionProvider action={action}>
            <MarketplaceEnabledProvider isMarketplaceEnabled={payload.isMarketplaceEnabled}>
              <CopilotSettingsProvider
                copilotAccessAllowed={payload.copilotAccessAllowed ?? false}
                copilotSpacesEnabled={payload.copilotSpacesEnabled ?? false}
                modelsAccessAllowed={payload.modelsAccessAllowed ?? false}
                modelsRepoIntegrationEnabled={payload.modelsRepoIntegrationEnabled ?? false}
              >
                <RefInfoProvider refInfo={refInfo}>
                  <EditPermissionsProvider
                    canEdit={refInfo.canEdit}
                    canEditOnDefaultBranch={refInfo.canEditOnDefaultBranch}
                    fileExistsOnDefault={refInfo.fileExistsOnDefault}
                  >
                    <AllShortcutsEnabledProvider allShortcutsEnabled={payload.allShortcutsEnabled}>
                      <FileTreeControlProvider>
                        <PermalinkShortcut />
                        <div>
                          <FileQueryProvider>
                            <OpenPanelProvider payload={payload} openPanelRef={openPanelRef}>
                              <SplitPageLayout>
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                                <div ref={treeRef} tabIndex={0} className={styles.treePane}>
                                  <ReposFileTreePane
                                    id={reposFileTreeId}
                                    repo={repo}
                                    path={path}
                                    isFilePath={isBlob || isEdit || isDelete}
                                    refInfo={refInfo}
                                    collapseTree={collapseTree}
                                    showTree={isTreeExpanded}
                                    fileTree={fileTree}
                                    onItemSelected={onTreeItemSelected}
                                    processingTime={payload.fileTreeProcessingTime}
                                    treeToggleElement={treeToggleElement}
                                    treeToggleRef={treeToggleRef}
                                    searchBoxRef={searchBoxRef}
                                    foldersToFetch={payload.foldersToFetch}
                                    incompleteFileTree={payload.incompleteFileTree}
                                    onFindFilesShortcut={onFindFilesShortcut}
                                    textAreaId={textAreaId}
                                    findFileWorkerPath={findFileWorkerPath}
                                    headerContent={
                                      <div className={styles.treeHeader}>
                                        {isTreeExpanded && treeToggleElement}
                                        <Heading as="h2" className={styles.treeHeading}>
                                          Files
                                        </Heading>
                                      </div>
                                    }
                                  />
                                </div>
                                <SplitPageLayout.Content
                                  as="div"
                                  padding="none"
                                  width={codeCenterOption.enabled ? 'xlarge' : 'full'}
                                  hidden={{narrow: isTreeExpanded}}
                                >
                                  <div
                                    className={styles.splitPaneContent}
                                    ref={setContentRef}
                                    data-selector="repos-split-pane-content"
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                    tabIndex={0}
                                  >
                                    <SearchTermProvider>
                                      <FindInFileOpenProvider isBlame={isBlame}>
                                        <CodeViewBannersProvider>
                                          <div className={clsx(isEdit && styles.headerHidden)}>
                                            <CodeViewHeader
                                              payload={payload}
                                              showTree={isTreeExpanded}
                                              treeToggleElement={treeToggleElement}
                                              validCodeNav={validCodeNav}
                                              onFindFilesShortcut={onFindFilesShortcut}
                                              copilotSWEAgentEnabled={payload.copilotSWEAgentEnabled}
                                            />
                                          </div>
                                          {payload.error ? (
                                            <CodeViewError {...payload.error} />
                                          ) : (
                                            <>
                                              <div
                                                className={clsx(
                                                  'react-code-view-bottom-padding',
                                                  styles.contentPadding,
                                                )}
                                              >
                                                <CodeViewBanners payload={payload} />
                                              </div>
                                              <div className={styles.contentPadding}>
                                                {isTreePayload(payload) ? (
                                                  <FileTreeViewContent
                                                    tree={payload.tree}
                                                    showTree={isTreeExpanded}
                                                    treeToggleElement={treeToggleElement}
                                                  />
                                                ) : isEditPayload(payload) ? (
                                                  <Suspense fallback={<LoadingFallback />}>
                                                    <BlobEditor
                                                      collapseTree={collapseTree}
                                                      editInfo={payload.editInfo}
                                                      repo={payload.repo}
                                                      showTree={isTreeExpanded}
                                                      treeToggleElement={treeToggleElement}
                                                      // we need a key so that edit -> new soft nav does a fresh initial render
                                                      key={`${payload.path}_${payload.editInfo.fileName}_${payload.editInfo.isNewFile}`}
                                                      webCommitInfo={payload.webCommitInfo}
                                                      copilotInfo={payload.copilotInfo}
                                                      copilotGenerateCommitMessageAuthInfo={
                                                        payload.copilotGenerateCommitMessageAuthInfo
                                                      }
                                                      isDataRouter={isDataRouter}
                                                    />
                                                  </Suspense>
                                                ) : isBlob ? (
                                                  <CurrentBlobProvider blob={payload.blob}>
                                                    <CurrentStyledBlobProvider blob={payload.blob}>
                                                      <CurrentBlameProvider blame={payload.blame}>
                                                        <BlobViewContent
                                                          blame={payload.blame}
                                                          blob={payload.blob}
                                                          symbolsExpanded={payload.symbolsExpanded}
                                                          setValidCodeNav={setValidCodeNav}
                                                          showTree={isTreeExpanded}
                                                          treeToggleElement={treeToggleElement}
                                                          validCodeNav={validCodeNav}
                                                          copilotInfo={payload.copilotInfo}
                                                        />
                                                      </CurrentBlameProvider>
                                                    </CurrentStyledBlobProvider>
                                                  </CurrentBlobProvider>
                                                ) : isDelete ? (
                                                  <DeleteViewContent
                                                    deleteInfo={payload.deleteInfo}
                                                    webCommitInfo={payload.webCommitInfo}
                                                  />
                                                ) : null}
                                              </div>
                                            </>
                                          )}
                                        </CodeViewBannersProvider>
                                      </FindInFileOpenProvider>
                                    </SearchTermProvider>
                                  </div>
                                </SplitPageLayout.Content>
                              </SplitPageLayout>
                            </OpenPanelProvider>
                          </FileQueryProvider>
                          <ScrollMarksContainer />
                          {/* TODO: make this focus on the cursor instead of just the blob as a whole */}
                          <DuplicateOnKeydownButton
                            buttonFocusId={textAreaId}
                            buttonHotkey={toggleFocusedPaneShortcut.hotkey}
                            onButtonClick={() => toggleFocus()}
                          />
                        </div>
                      </FileTreeControlProvider>
                    </AllShortcutsEnabledProvider>
                  </EditPermissionsProvider>
                </RefInfoProvider>
              </CopilotSettingsProvider>
            </MarketplaceEnabledProvider>
          </ActionProvider>
        </PathProvider>
      </DeferredASTProvider>
    </DeferredMetadataProvider>
  )
}
